import useDocumentMeta from "../hooks/useDocumentMeta.js";
import usePageLanguage from "../hooks/usePageLanguage.js";
import { alternatesFor, canonicalUrlFor } from "../content/pages/registry.js";
import usePageShell from "../hooks/usePageShell.js";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import CookieBanner from "../components/common/CookieBanner";
import useAnalyticsContext from "../hooks/useAnalyticsContext.js";
import { trackSectionViewed, useSectionOnceObserver } from "../lib/analytics";
import PhotoSharingEditorial from "../components/wedding-photo-sharing/PhotoSharingEditorial";
import RsvpHero from "../components/wedding-rsvp/RsvpHero";
import RsvpFlow from "../components/wedding-rsvp/RsvpFlow";
import RsvpDemo from "../components/wedding-rsvp/RsvpDemo";
import RsvpPlan from "../components/wedding-rsvp/RsvpPlan";
import RsvpFaq from "../components/wedding-rsvp/RsvpFaq";
import RsvpFinalCta from "../components/wedding-rsvp/RsvpFinalCta";
import { WRS_SECTION } from "../components/wedding-rsvp/sections.js";

/**
 * /wedding-rsvp
 *
 * The fourth SEO/GEO page. /digital-wedding-invitation is what a guest opens,
 * this is what they do inside it, /wedding-photo-sharing is the day itself and
 * /wedding-planning the work running underneath all of it. Same visual language
 * on purpose — it should read as another chapter, not a different site.
 *
 * ── Crawlability ──────────────────────────────────────────────────────────
 * This route also ships as a real static document (`wedding-rsvp.html` at the
 * project root, built as a fifth Vite entry). That file — not this component —
 * is what a crawler that does not run JavaScript receives, and it carries the
 * title, description, canonical, robots directive, social tags, JSON-LD and
 * every heading, paragraph and FAQ answer below. React replaces it on mount.
 * `weddingRsvp.seo.test.mjs` asserts the two never drift apart, and pins the
 * claims the product audit ruled out.
 *
 * The meta hook still runs, because a visitor who arrives by client-side
 * navigation gets no static head.
 *
 * ── The claim boundaries that govern the whole page ───────────────────────
 * Five, and every one of them is checked in the test rather than trusted:
 *
 *  1. **Heirloom-only.** `PLAN_FEATURE_MATRIX.invitationProduct` is true for
 *     `pro` alone and names RSVP, the guest list, event sections,
 *     send-invitations and seating as one entitlement
 *     (api/constants/planFeatures.mjs); the RSVP and guests routers are both
 *     mounted behind `requireProPlanWithCode`. A Blossom or Bouquet couple has
 *     no RSVP at all — not a smaller one. `RsvpPlan` is what keeps the rest of
 *     the page honest, which is why it sits before the FAQ rather than after.
 *  2. **A guest replies once.** `POST …/rsvp/:code` 409s on an already
 *     submitted guest (`RSVP_ALREADY_SUBMITTED`) and the loader returns a
 *     read-only recap. Changes are the couple's to make, through
 *     `PUT /guests/:guestId/attendance`. Nothing here may suggest a guest can
 *     keep editing their answer.
 *  3. **Nothing counts until the couple approves it.** A guest-added companion
 *     is written `reviewStatus: "pendingReview"`; every headcount, per-section
 *     count and the seating pool filter to approved members only. No sentence
 *     may read as an automatic plus-one.
 *  4. **The RSVP asks two things.** Who is coming, and to which moments —
 *     `rsvpSubmissionSchema` has no dietary field and there is no question
 *     model anywhere in the product. Dietary requirements exist, but as
 *     something the COUPLE keeps on a party member.
 *  5. **The language claim is about the email.** `guest.preferredLanguage`
 *     drives `sendGuestInvitation`; the personal link carries no `?lang=`, so
 *     the page says the email is sent in the guest's language and that the
 *     invitation is written in both — never that the page opens pre-switched.
 *
 * ── Page shell ────────────────────────────────────────────────────────────
 * `usePageShell` — the shared bootstrap every page runs. See the hook for why
 * it re-initialises the Webflow engine from a layout effect rather than after
 * paint.
 *
 * `wps-page dwi-page` on the wrapper, not a fourth scope class: the invitation
 * page's `dwi-demo` block is exactly the framed offer this page needs for the
 * sample RSVP, and the photo-sharing page's `wps-flow` is exactly the ordered
 * sequence the guest-list section needs. Nothing here is new enough to earn a
 * page-specific layout prefix, and the one page-specific class that does exist
 * — the pre-render language-guard marker — lives in the static document alone.
 * A copy of it here would hide the real page from every Greek visitor.
 */
/**
 * This page's language-neutral path. The canonical and the alternates are
 * derived from it through the page registry rather than written out: the
 * canonical is self-referencing (a Greek page canonicalised to English is a
 * Greek page asking not to be indexed), and the alternates are the reciprocal
 * set the static documents declare, from the same one source.
 */
const PATH = "/wedding-rsvp";

const WeddingRsvp = () => {
  const analyticsContext = useAnalyticsContext();

  const language = usePageLanguage();

  useDocumentMeta({
    titleKey: "meta.weddingRsvp.title",
    descriptionKey: "meta.weddingRsvp.description",
    canonical: canonicalUrlFor(PATH, language),
    alternates: alternatesFor(PATH),
    robots: "index, follow",
  });

  // Attendance-per-moment is the differentiator the page is really selling, so
  // it gets its own impression. `PhotoSharingEditorial` stays presentational —
  // the observer rides on a wrapper here rather than being pushed into a
  // component four pages share.
  const momentsRef = useSectionOnceObserver(() =>
    trackSectionViewed(WRS_SECTION.MOMENTS, analyticsContext),
  );
  const guestListRef = useSectionOnceObserver(() =>
    trackSectionViewed(WRS_SECTION.GUEST_LIST, analyticsContext),
  );

  usePageShell();

  return (
    <div className="page-wrapper wps-page dwi-page">
      <Navbar />
      <CookieBanner />
      <main className="main">
        <RsvpHero />

        <PhotoSharingEditorial
          headingKey="weddingRsvp.overview.heading"
          bodyKeys={[
            "weddingRsvp.overview.body1",
            "weddingRsvp.overview.body2",
            "weddingRsvp.overview.body3",
          ]}
          noteKey="weddingRsvp.overview.note"
          image={{
            src: "/assets/images/invitation/invitation-1.jpg",
            altKey: "weddingRsvp.overview.imageAlt",
            width: 6558,
            height: 3659,
          }}
        />

        <div ref={momentsRef}>
          <PhotoSharingEditorial
            headingKey="weddingRsvp.moments.heading"
            bodyKeys={[
              "weddingRsvp.moments.body1",
              "weddingRsvp.moments.body2",
              "weddingRsvp.moments.body3",
            ]}
            noteKey="weddingRsvp.moments.note"
            image={{
              src: "/assets/images/moments/moment_3.jpg",
              altKey: "weddingRsvp.moments.imageAlt",
              width: 6558,
              height: 3659,
            }}
            imageSide="left"
          />
        </div>

        <PhotoSharingEditorial
          headingKey="weddingRsvp.personal.heading"
          bodyKeys={[
            "weddingRsvp.personal.body1",
            "weddingRsvp.personal.body2",
          ]}
          noteKey="weddingRsvp.personal.note"
          image={{
            src: "/assets/images/moments/moment-2.jpg",
            altKey: "weddingRsvp.personal.imageAlt",
            width: 6983,
            height: 3436,
          }}
        />

        <PhotoSharingEditorial
          headingKey="weddingRsvp.companions.heading"
          bodyKeys={[
            "weddingRsvp.companions.body1",
            "weddingRsvp.companions.body2",
          ]}
          noteKey="weddingRsvp.companions.note"
        />

        <div ref={guestListRef}>
          <RsvpFlow />
        </div>

        <PhotoSharingEditorial
          headingKey="weddingRsvp.beyond.heading"
          bodyKeys={[
            "weddingRsvp.beyond.body1",
            "weddingRsvp.beyond.body2",
            "weddingRsvp.beyond.body3",
          ]}
          image={{
            src: "/assets/images/moments/moment-3.jpg",
            altKey: "weddingRsvp.beyond.imageAlt",
            width: 6983,
            height: 3436,
          }}
          imageSide="left"
        />

        <RsvpDemo />

        <RsvpPlan />

        <RsvpFaq />

        <RsvpFinalCta />
      </main>
      <Footer />
    </div>
  );
};

export default WeddingRsvp;
